import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const response = await authService.getProfile();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error checking auth:', error);
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      const { token, user } = response.data;
      localStorage.setItem('auth_token', token);
      setUser(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.log('Login error:', error.response); // Debug

      // Si es error 401, siempre es credenciales incorrectas
      if (error.response?.status === 401) {
        return {
          success: false,
          error: 'Email o contraseña incorrectos'
        };
      }

      // Otros errores
      let errorMessage = 'Error al iniciar sesión';

      if (error.response?.data?.message) {
        const backendMessage = error.response.data.message.toLowerCase();

        if (backendMessage.includes('invalid credentials') ||
            backendMessage.includes('credenciales') ||
            backendMessage.includes('incorrecta')) {
          errorMessage = 'Email o contraseña incorrectos';
        } else if (backendMessage.includes('not found') ||
                   backendMessage.includes('no encontrado')) {
          errorMessage = 'No existe una cuenta con este email';
        } else if (backendMessage.includes('blocked') ||
                   backendMessage.includes('bloqueado')) {
          errorMessage = 'Tu cuenta está bloqueada';
        } else {
          errorMessage = error.response.data.message;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const { token, user } = response.data;
      localStorage.setItem('auth_token', token);
      setUser(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      // Traducir y mejorar mensajes de error del backend
      let errorMessage = 'Error al registrarse';

      if (error.response?.data?.message) {
        const backendMessage = error.response.data.message.toLowerCase();

        if (backendMessage.includes('already exists') ||
            backendMessage.includes('ya existe') ||
            backendMessage.includes('duplicate')) {
          errorMessage = 'Ya existe una cuenta con este email';
        } else if (backendMessage.includes('invalid email') ||
                   backendMessage.includes('email inválido')) {
          errorMessage = 'El formato del email no es válido';
        } else if (backendMessage.includes('password') &&
                   (backendMessage.includes('weak') || backendMessage.includes('débil'))) {
          errorMessage = 'La contraseña es demasiado débil';
        } else {
          errorMessage = error.response.data.message;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (updatedUserData) => {
    setUser((prevUser) => ({ ...prevUser, ...updatedUserData }));
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
